import { deriveIntentForPlanningReport } from '../../../lib/generationImpact/selectedAssessmentIntent';
/**
 * Project Intent / Overview — narrative prose + property map with item-level layers.
 */
import React from 'react';
import type { SiteRagReport } from '../../../types/siteRag';
import type { ReportTheme } from '../../../types/reportTheme';
import type { SiteEntity, SiteLayer } from '../../../types/site';
import { resolveProjectIntentOverviewProse } from '../../../lib/buildProjectIntentOverview';
import { EditorialSectionShell } from './EditorialSectionShell';
import {
  REPORT_SECTION_HEADING_TEXT_CLASS,
  reportSectionHeadingStyle, reportTypographySizes, REPORT_TEXT_PX } from '../../../lib/reportSectionHeading';
import { EditableHeading } from './EditableHeading';
import { PropertyMapContent } from './PropertyMapContent';
import { KeyFindingsEmptyState } from './KeyFindingsEmptyState';
import { ContextualKeyFindingsBlock } from './ContextualKeyFindingsBlock';
import { SectionQuarantineBanner } from './SectionQuarantineBanner';
import { walkClaimsText } from '../../../lib/walkClaimsText';
import { walkClaimText } from '../../../lib/walkClaimText';
import { NUMBERED_REF_TOKEN_RE, extractNumberedRefIds } from '../../../lib/citationTokens';
import { SavedScreeningProse } from '../overview/SavedScreeningProse';
import { filterRenderableParagraphs } from '../../../lib/safeNarrativeProse';
import { EditorialDatasheet, type DatasheetRow } from './editorial';
import { EditorialProseBody } from './editorial/EditorialProseBody';
import { hasGeneratedEditorialLayer } from '../../../lib/editorial/resolveEditorialLayer';
import { roleColor } from '../../../lib/reportRoleColors';
import { referenceSavedPowerOptionToVisibleActionPlan } from '../../../lib/reportGeneration/actionPlanPresentation';
import {
  buildAdaptiveProjectIntentOverviewModel,
  resolveReportScreeningMode,
} from '../../../lib/projectIntentOverviewModel';

export { resolveReportScreeningMode };

export interface ProjectIntentOverviewSectionProps {
  report: SiteRagReport;
  theme: ReportTheme;
  reportSite?: SiteEntity;
  reportSiteLayers: SiteLayer[];
  propertyMapSurfaceMode: 'interactive' | 'static';
  onEditLayers?: () => void;
  isAdmin?: boolean;
  onSectionRegen?: (sectionId: string) => Promise<void> | void;
  isEditing?: boolean;
  headingOverrides?: Record<string, string>;
  onHeadingChange?: (key: string, value: string) => void;
  sectionDefaultHeading: (key: string, fallback: string) => string;
  sectionStyle: React.CSSProperties;
  sectionPageColumnProps?: Record<string, unknown>;
  shellPadding?: boolean;
  omitSectionSidebar?: boolean;
  extraClassName?: string;
  exportMode?: boolean;
  /** RC4 — the un-merged theme-preset id, forwarded to the site-map figure so
   *  Monograph framing is selected by preset id, not the heading-font sniff. */
  presetId?: string | null;
  /** Part L2 — when true (VITE_EDITORIAL_LAYOUTS on), prepend the editorial
   *  hero-numeral opener above the heading so this inline section matches the
   *  router-rendered ones. Default false ⇒ byte-stable (nothing added). */
  editorialActive?: boolean;
  /** Part L2 — the 1-based section index for the hero numeral (from the same
   *  sectionDisplayNumbers the TOC + router consume). Out-of-range ⇒ no numeral. */
  editorialSectionNumber?: number;
  /** Full-report-only target. Standalone/brief overview exports omit this. */
  actionPlanAppendix?: { visible: boolean; heading: string };
}

function resolveOverviewProse(report: SiteRagReport): string | null {
  return resolveProjectIntentOverviewProse({
    ...report,
    projectIntentAtGen: deriveIntentForPlanningReport(report, report.projectIntentAtGen),
  });
}

function overviewParagraphs(overview: string): string[] {
  return filterRenderableParagraphs(overview).split(/\n\n+/).filter(Boolean);
}

/** Resolve known citations through the full report's appendix. Preserve an
 * unmatched saved marker visibly; a layout repair must not erase provenance. */
function renderSavedRecommendationText(value: string, report: SiteRagReport): React.ReactNode {
  const splitter = new RegExp(`(${NUMBERED_REF_TOKEN_RE.source})`, 'gi');
  return value.split(splitter).map((part, index) => {
    const ids = extractNumberedRefIds(part);
    const resolvable =
      ids.length > 0 && ids.every(id => id > 0 && Boolean(report.references?.[id - 1]));
    return (
      <React.Fragment key={index}>
        {resolvable ? walkClaimText(part, report.references) : part}
      </React.Fragment>
    );
  });
}

type ReportWithSectionQuarantine = SiteRagReport & {
  sectionQuarantine?: Record<string, unknown>;
};

export const ProjectIntentOverviewSection: React.FC<ProjectIntentOverviewSectionProps> = ({
  report,
  theme,
  reportSite,
  reportSiteLayers,
  propertyMapSurfaceMode,
  onEditLayers,
  isAdmin,
  onSectionRegen,
  isEditing = false,
  headingOverrides,
  onHeadingChange,
  sectionDefaultHeading,
  sectionStyle,
  sectionPageColumnProps,
  shellPadding,
  omitSectionSidebar,
  extraClassName,
  exportMode = false,
  presetId,
  editorialActive = false,
  editorialSectionNumber,
  actionPlanAppendix,
}) => {
  const overview = resolveOverviewProse(report);
  const overviewParas = overview ? overviewParagraphs(overview) : [];
  const quarantineMap = (report as ReportWithSectionQuarantine).sectionQuarantine;
  const quarantined =
    quarantineMap?.projectIntentOverview != null || quarantineMap?.keyFindings != null;
  const placeholderCopy = quarantined
    ? 'This section was flagged for review by an upstream quality check. See the quarantine banner above for details.'
    : !report.projectIntentAtGen
      ? 'Set Project Intent in site configuration, then rerun the report to populate this overview.'
      : 'Project intent overview was not generated for this report. Run a full rerun to regenerate this section.';

  const showExecSummaryFindings = report.reportType === 'executive-summary';
  const model = buildAdaptiveProjectIntentOverviewModel(report, reportSite);
  const {
    screeningMode,
    showModeScorecard,
    specRailItems,
    recordedBriefRows,
    recommendationStatus,
    powerRecommendation,
    supportingRecommendations,
    rationaleRows,
    alternativesList,
    evidenceGaps,
    proposalSummaryLead,
    proposalRows,
    proposalAlternative,
    preservedUserDecisions,
    intentDrift,
  } = model;
  const displayedPowerOption = actionPlanAppendix
    ? referenceSavedPowerOptionToVisibleActionPlan(report, model, { ...actionPlanAppendix, isEditing })
    : powerRecommendation.value;

  const recommendationTypography = reportTypographySizes(theme);
  const recommendationBodyStyle: React.CSSProperties = {
    fontFamily: 'var(--report-body-font, inherit)',
    fontSize: recommendationTypography.body,
    color: roleColor(theme, 'body'),
  };

  // WAVE 3 — §01 prose-led monograph. When a GENERATED editorial layer exists,
  // the flowing EditorialProseBody (drop-cap + pull-quote) re-voices the brief,
  // and the existing `overviewParas` prose is SUPPRESSED so it is not doubled.
  // The "At a glance" spec rail + the site map are EXHIBITS and always render.
  // No layer ⇒ byte-identical (the reportSurfaceParity fixtures carry none).
  const layerPresent = hasGeneratedEditorialLayer(report, 'projectIntentOverview');

  // N1.5C — the heading node is SHARED between the two shell modes so the
  // TOC anchor / edit wiring are identical on both paths. In editorial mode the
  // shell owns the opener <h2>; in classic mode the caller renders it below.
  const headingNode = (
    <EditableHeading
      headingKey="projectIntentOverview"
      defaultText={sectionDefaultHeading('projectIntentOverview', 'Project Intent & Site Overview')}
      className={REPORT_SECTION_HEADING_TEXT_CLASS}
      isEditing={isEditing}
      overrides={headingOverrides ?? {}}
      onChange={onHeadingChange ?? (() => {})}
    />
  );

  return (
    <EditorialSectionShell
      id="projectIntentOverview"
      data-section="projectIntentOverview"
      data-section-id="projectIntentOverview"
      data-executive-project-overview={showExecSummaryFindings ? 'true' : undefined}
      style={sectionStyle}
      className="space-y-4"
      shellPadding={shellPadding}
      omitSectionSidebar={omitSectionSidebar}
      theme={theme}
      extraClassName={extraClassName}
      {...sectionPageColumnProps}
      editorialActive={editorialActive}
      sectionNumber={editorialSectionNumber}
      heading={headingNode}
    >
      {/* Classic mode (flag OFF) — the caller-owned head renders byte-identically
           to the retired ClassicSectionShell path. Editorial mode (flag ON): the
           shell owns the opener (hero numeral + numbered head), so this is
           suppressed. */}
      {!editorialActive && (
        <header className="space-y-3">
          {/* P2.4 — heading colour/font routed through the canonical helper so
               a theme preset reskins it. This variant keeps its own `m-0 pb-0`
               (no bottom rule) className; reportSectionHeadingStyle only adds a
               `borderColor` which is a no-op without a border width, so the
               legacy render is byte-identical. */}
          <h2
            className={`${REPORT_SECTION_HEADING_TEXT_CLASS} m-0 pb-0`}
            style={reportSectionHeadingStyle(theme)}
            data-heading="true"
          >
            {headingNode}
          </h2>
        </header>
      )}

      <SectionQuarantineBanner
        sectionId="projectIntentOverview"
        sectionLabel="Project Intent & Site Overview"
        guardStatus={report.sectionGuardStatus?.projectIntentOverview}
        quarantinedOriginal={report.quarantinedOriginal?.projectIntentOverview}
        renderedText={overview ?? report.projectIntentOverview}
        onRerun={onSectionRegen ? () => onSectionRegen('projectIntentOverview') : undefined}
      />

      {intentDrift.hasModeDrift && (
        <div
          data-testid="project-intent-screening-mode-drift"
          role="status"
          className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3.5 py-2.5 text-xs text-amber-950 dark:text-amber-200"
        >
          <strong>Intent drift:</strong> This frozen report was generated in{' '}
          <strong>
            {intentDrift.genMode === 'evaluate-proposal'
              ? 'Evaluate my proposal (evaluate-proposal)'
              : 'Recommend an approach (recommend)'}
          </strong>{' '}
          mode, while live Project Intent is set to{' '}
          <strong>
            {intentDrift.liveMode === 'evaluate-proposal'
              ? 'Evaluate my proposal (evaluate-proposal)'
              : 'Recommend an approach (recommend)'}
          </strong>
          . Rerun Screening Assessment to align the analysis and PDF export with the current Project Intent.
        </div>
      )}

      {intentDrift.hasInputDrift && (
        <div
          data-testid="project-intent-stale-input-banner"
          role="status"
          className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3.5 py-2.5 text-xs text-amber-950 dark:text-amber-200"
        >
          <strong>Stale screening snapshot:</strong> {intentDrift.summaryText}{' '}
          Rerun Screening Assessment to evaluate updated Project Intent fields against verified site evidence.
        </div>
      )}

      {/* SECTION-PORT (3B, "brief + spec rail") — the locked Intent layout
           leads with an "at a glance" spec rail that summarises the gen-time
           project-intent snapshot + site geometry, composed BENEATH the
           head/banner and ABOVE the existing prose+map brief (all of which is
           preserved untouched). The rail is theme-token-driven (renders across
           journal/institutional/monograph), and each entry is dropped when its
           source value is absent, so a thin report shows a shorter ledger — no
           fabricated specs.
           J5 — rendered as a COMPACT dotted-leader EditorialDatasheet (single
           headless group: key … value per row) rather than the taller stacked
           EditorialDefinitionList, so the Overview map + brief fit on ONE page.
           GAP: none material — the parcel-boundary figure is already provided
           by the existing PropertyMapContent block below (boundary geometry),
           so the MAP's 3B SVG exhibit is NOT duplicated here. */}
      {specRailItems.length > 0 && !(showModeScorecard && screeningMode === 'recommend') && (
        <div
          data-testid="project-intent-spec-rail"
          style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}
        >
          <div
            style={{
              fontFamily: 'var(--report-body-font, inherit)',
              fontSize: REPORT_TEXT_PX.label,
              fontWeight: 600,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: roleColor(theme, 'body'),
              opacity: 0.7,
              marginBottom: 4,
              breakAfter: 'avoid',
              pageBreakAfter: 'avoid',
            }}
          >
            At a glance
          </div>
          <EditorialDatasheet theme={theme} groups={[{ rows: specRailItems }]} />
        </div>
      )}

      {showModeScorecard && (
        <div
          data-testid="screening-mode-scorecard"
          data-screening-mode={screeningMode}
          className={`min-w-0 rounded-md border print:shadow-none ${screeningMode === 'recommend' ? 'space-y-5 p-4 sm:p-5 print:p-0 print:border-0' : 'p-3.5 space-y-2.5 break-inside-avoid'}`}
          style={{ color: roleColor(theme, 'body'), borderColor: roleColor(theme, 'hair') }}
        >
          {screeningMode === 'recommend' ? (
            <>
              <header className="space-y-2 [break-after:avoid]">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="m-0 font-semibold leading-snug" style={{ fontFamily: theme.headingFont,
                    fontSize: recommendationTypography.h3, color: roleColor(theme, 'ink') }}>
                    Recommended Site Architecture &amp; Supportable Envelope
                  </h3>
                  <span
                    data-testid="screening-recommendation-status"
                    data-recommendation-status={recommendationStatus.status}
                    className={`inline-flex items-center rounded border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                      recommendationStatus.status === 'recommended'
                        ? 'bg-emerald-500/15 text-emerald-900 border-emerald-500/30'
                        : recommendationStatus.status === 'conditional'
                          ? 'bg-amber-500/15 text-amber-900 border-amber-500/30'
                          : 'bg-rose-500/15 text-rose-900 border-rose-500/30'
                    }`}
                  >
                    {recommendationStatus.label}
                  </span>
                </div>
                <p className="m-0 leading-relaxed" style={recommendationBodyStyle}>
                  {recommendationStatus.explanation}
                </p>
              </header>
              <p className="m-0 border-l-2 px-3 py-2 leading-relaxed" style={{ ...recommendationBodyStyle,
                borderColor: roleColor(theme, 'accent'), backgroundColor: roleColor(theme, 'accentSoft') }}>
                <strong>Intent: Recommend an approach.</strong>{' '}
                Review this screening option before adopting it. Recorded brief inputs remain separate.
              </p>
              <dl data-testid="screening-power-summary" className="min-w-0 space-y-2">
                <dt style={{ ...recommendationBodyStyle, color: roleColor(theme, 'muted') }}>Saved power option</dt>
                <dd className="m-0 min-w-0">
                  <SavedScreeningProse text={displayedPowerOption} className="font-semibold leading-snug"
                    style={{ fontFamily: theme.headingFont, fontSize: recommendationTypography.h3, color: roleColor(theme, 'ink') }}
                    renderPlainText={value => renderSavedRecommendationText(value, report)} />
                </dd>
              </dl>
              <dl data-testid="screening-supporting-facts" className="flex min-w-0 flex-wrap gap-x-6 border-y py-4 print:block"
                style={{ borderColor: roleColor(theme, 'hair') }}>
                {supportingRecommendations.map(row => (
                  <div key={row.id} className="min-w-0 flex-1 basis-56 space-y-2 py-2 print:mb-3">
                    <dt className="[break-after:avoid]" style={{ ...recommendationBodyStyle, color: roleColor(theme, 'muted') }}>
                      {row.label}
                    </dt>
                    <dd className="m-0 min-w-0">
                      <SavedScreeningProse text={row.value} className="font-semibold leading-relaxed" style={recommendationBodyStyle}
                        renderPlainText={value => renderSavedRecommendationText(value, report)} />
                    </dd>
                  </div>
                ))}
              </dl>
              {rationaleRows.length > 0 && (
                <section data-testid="screening-recommendation-rationale" className="min-w-0 space-y-4 border-t pt-4"
                  style={{ borderColor: roleColor(theme, 'hair') }}>
                  <h4 className="m-0 font-semibold [break-after:avoid]" style={recommendationBodyStyle}>Saved rationale and conditions</h4>
                  {rationaleRows.map(row => (
                    <div key={row.id} data-testid="screening-recommend-row" data-recommendation={row.id}
                      className="min-w-0 space-y-2 [break-inside:auto]">
                      {rationaleRows.length > 1 && <h5 className="m-0 font-semibold [break-after:avoid]" style={recommendationBodyStyle}>{row.label}</h5>}
                      {row.notes.map(note => (
                        <SavedScreeningProse key={note} text={note} className="leading-relaxed [orphans:3] [widows:3]"
                          style={recommendationBodyStyle} renderPlainText={value => renderSavedRecommendationText(value, report)} />
                      ))}
                    </div>
                  ))}
                </section>
              )}
              {alternativesList.length > 0 && (
                <section data-testid="screening-recommendation-alternatives" className="min-w-0 space-y-2 border-t pt-4"
                  style={{ borderColor: roleColor(theme, 'hair') }}>
                  <h4 className="m-0 font-semibold [break-after:avoid]" style={recommendationBodyStyle}>Evaluated alternatives</h4>
                  <ul className="m-0 list-disc pl-5 space-y-1.5" style={recommendationBodyStyle}>
                    {alternativesList.map((alt, idx) => (
                      <li key={`${idx}-${alt}`}>
                        <SavedScreeningProse text={alt} className="leading-relaxed" style={recommendationBodyStyle}
                          renderPlainText={value => renderSavedRecommendationText(value, report)} />
                      </li>
                    ))}
                  </ul>
                </section>
              )}
              {evidenceGaps.length > 0 && (
                <section data-testid="screening-recommendation-gaps" className="min-w-0 space-y-2.5 border-t pt-4"
                  style={{ borderColor: roleColor(theme, 'hair') }}>
                  <h4 className="m-0 font-semibold [break-after:avoid]" style={recommendationBodyStyle}>Material evidence gaps &amp; Stage 2 next steps</h4>
                  <div className="space-y-2">
                    {evidenceGaps.map(gap => (
                      <div key={gap.id} data-testid="screening-recommendation-gap-row" className="rounded border px-3 py-2 space-y-1"
                        style={{ borderColor: roleColor(theme, 'hair') }}>
                        <div className="text-xs font-semibold">{gap.label}</div>
                        <SavedScreeningProse text={gap.gap} className="text-xs leading-relaxed" style={recommendationBodyStyle}
                          renderPlainText={value => renderSavedRecommendationText(value, report)} />
                        <p className="m-0 text-xs font-medium" style={{ color: roleColor(theme, 'muted') }}>
                          <strong>Next step:</strong> {gap.nextStep}
                        </p>
                      </div>
                    ))}
                  </div>
                </section>
              )}
              <section data-testid="screening-recorded-brief" className="min-w-0 space-y-3 border-t pt-4"
                style={{ borderColor: roleColor(theme, 'hair') }}>
                <h4 className="m-0 font-semibold [break-after:avoid]" style={recommendationBodyStyle}>Recorded brief constraints</h4>
                {recordedBriefRows.length > 0 ? (
                  <div data-testid="project-intent-spec-rail">
                    <EditorialDatasheet theme={theme} groups={[{ rows: recordedBriefRows }]} />
                  </div>
                ) : <p className="m-0 leading-relaxed" style={recommendationBodyStyle}>No fixed architecture constraints were recorded in the saved brief.</p>}
                <p className="m-0 leading-relaxed" style={{ ...recommendationBodyStyle, color: roleColor(theme, 'muted') }}>
                  These are saved project inputs, not a recommended size range or an accepted architecture.
                </p>
              </section>
              {preservedUserDecisions.length > 0 && (
                <section data-testid="screening-preserved-user-decisions" className="min-w-0 space-y-1.5 border-t pt-3"
                  style={{ borderColor: roleColor(theme, 'hair') }}>
                  <h4 className="m-0 text-xs font-semibold uppercase tracking-wider" style={{ color: roleColor(theme, 'muted') }}>
                    Preserved User Decisions
                  </h4>
                  <ul className="m-0 list-disc pl-5 text-xs space-y-1" style={recommendationBodyStyle}>
                    {preservedUserDecisions.map((decision, idx) => (
                      <li key={`${idx}-${decision}`}>{decision}</li>
                    ))}
                  </ul>
                </section>
              )}
              <p className="m-0 border-t pt-3 leading-relaxed" style={{ ...recommendationBodyStyle,
                color: roleColor(theme, 'muted'), borderColor: roleColor(theme, 'hair') }}>
                Recommendations remain advisory until adopted into Project Intent and verified in Stage 2.
              </p>
            </>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span
                    className="inline-flex items-center rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-indigo-500/15 text-indigo-900 border border-indigo-500/30"
                    style={{ marginRight: 8 }}
                  >
                    Evaluate My Proposal
                  </span>{' '}
                  <span className="text-xs font-semibold">
                    Proposal vs. Verified Site Evidence Scorecard
                  </span>
                </div>
                <span className="text-[11px] opacity-75">
                  Stress-testing declared targets against site constraints
                </span>
              </div>
              <p
                data-testid="screening-proposal-summary"
                className="m-0 text-xs leading-relaxed border-l-2 pl-2.5 py-1"
                style={{ borderColor: roleColor(theme, 'accent') }}
              >
                {proposalSummaryLead}
              </p>
              {proposalRows.length > 0 && (
                <div className="overflow-x-auto pt-1">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead>
                      <tr className="border-b border-border-subtle/70 text-[10px] uppercase tracking-wider opacity-75">
                        <th className="py-1.5 pr-3 font-semibold">Requirement</th>
                        <th className="py-1.5 pr-3 font-semibold">Declared Proposal</th>
                        <th className="py-1.5 pr-3 font-semibold">Verified Site Evidence</th>
                        <th className="py-1.5 pr-3 font-semibold">Gap &amp; Next Step</th>
                        <th className="py-1.5 text-right font-semibold">Alignment</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border-subtle/40">
                      {proposalRows.map((row) => (
                        <tr
                          key={row.dimension}
                          data-testid="screening-proposal-row"
                          data-assessment-status={row.assessmentStatus}
                        >
                          <td className="py-1.5 pr-3 font-semibold align-top whitespace-nowrap">
                            {row.dimension}
                          </td>
                          <td className="py-1.5 pr-3 align-top">
                            {row.declaredProposal}
                          </td>
                          <td className="py-1.5 pr-3 align-top opacity-90">
                            <SavedScreeningProse
                              text={row.verifiedEvidence}
                              className="text-xs leading-snug"
                              renderPlainText={value => renderSavedRecommendationText(value, report)}
                            />
                          </td>
                          <td
                            data-testid="screening-proposal-gap-next-step"
                            className="py-1.5 pr-3 align-top text-[11px] opacity-85"
                          >
                            <div>{row.gapSummary}</div>
                            <div className="font-medium mt-0.5">Next step: {row.nextStep}</div>
                          </td>
                          <td className="py-1.5 text-right align-top whitespace-nowrap">
                            <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${row.badge.className}`}>
                              {row.badge.label}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {proposalAlternative && (
                <div
                  data-testid="screening-proposal-alternative"
                  className="rounded border px-3 py-2 text-xs space-y-1"
                  style={{
                    borderColor: roleColor(theme, 'hair'),
                    backgroundColor: roleColor(theme, 'accentSoft'),
                  }}
                >
                  <div className="font-semibold">
                    {proposalAlternative.label}: {proposalAlternative.summary}
                  </div>
                  {proposalAlternative.rationale && (
                    <SavedScreeningProse
                      text={proposalAlternative.rationale}
                      className="text-xs leading-relaxed opacity-90"
                      renderPlainText={value => renderSavedRecommendationText(value, report)}
                    />
                  )}
                </div>
              )}
              {preservedUserDecisions.length > 0 && (
                <div
                  data-testid="screening-preserved-user-decisions"
                  className="border-t pt-2 text-xs space-y-1"
                  style={{ borderColor: roleColor(theme, 'hair') }}
                >
                  <div className="font-semibold uppercase tracking-wider text-[10px] opacity-75">
                    Preserved User Decisions
                  </div>
                  <ul className="m-0 list-disc pl-4 space-y-0.5">
                    {preservedUserDecisions.map((decision, idx) => (
                      <li key={`${idx}-${decision}`}>{decision}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      )}

      <div
        // DEFECT 1c — FLATTEN TO CREAM: strip the grey `bg-surface-1` card
        // fill, hairline border, rounding + shadow so the prose + map sit BARE
        // on the cream page. `break-inside-avoid` is retained (HR-QA fix
        // 2026-06-10 — the prose + map + legend never split mid-block across
        // PDF pages; ignored by CSS fragmentation when it exceeds a full page).
        className="break-inside-avoid print:shadow-none"
        data-testid="project-intent-overview-panel"
      >
        {/* P2.2 — responsive grid (prose | map) instead of a lg:float-right
             map. The map cell self-starts at the top so it pins to the
             section head while the prose flows in its own 1fr column. This
             kills the L-shaped wasted gap that the old float left under short
             prose (and never orphans the map on long prose, since the columns
             are independent). Below lg, stack with the map after the prose
             (flex-col-reverse keeps the prior mobile order). The data passed
             to PropertyMapContent is unchanged. */}
        {/* DEFECT 7 — tighter gutters (no card padding) so the overview fits ONE
            A4 page: heading + narrative + map + caption. */}
        <div className="flex flex-col-reverse gap-4 lg:gap-0 lg:grid lg:grid-cols-[1fr_23rem]">
          <div
            // DEFECT 1c — strip the grey `bg-surface-2/50` mobile fill + border;
            // the map block flows bare on cream. (The lg: breakpoint already
            // dropped the fill — this also clears the mobile/print wash.)
            className="mb-0 mt-4 lg:mt-0 lg:mb-0 lg:self-start lg:pl-6 p-0 lg:order-2"
            data-testid="property-map-block"
            data-surface-mode={propertyMapSurfaceMode}
          >
            <div className="text-[10px] font-bold uppercase tracking-widest text-text-muted mb-2 lg:mb-3">
              Site overview
            </div>
            <PropertyMapContent
              report={report}
              reportSite={reportSite}
              reportSiteLayers={reportSiteLayers}
              surfaceMode={propertyMapSurfaceMode}
              onEditLayers={onEditLayers}
              layout="projectIntentOverview"
              theme={theme}
              presetId={presetId}
              editorialActive={editorialActive}
            />
          </div>

          {/* William QA (2026-06-17) — force the WCAG-clamped dark body ink
              (roleColor 'body') when themed so the overview prose reads BLACK on
              the light report (theme-appropriate dark in any theme), matching
              every other section. Without it the <p> children inherited the muted
              slate bodyTextColor and rendered near-invisible. */}
          <div
            className="text-sm leading-relaxed text-text-primary space-y-3 lg:order-1 lg:min-w-0"
            style={theme ? { color: roleColor(theme, 'body') } : undefined}
            data-testid="project-intent-overview-content"
          >
            {/* WAVE 3 — flowing editorial prose body when a generated layer is
                present (re-voices the brief); the structured `overviewParas`
                prose is suppressed in tandem so it is not doubled. */}
            {layerPresent ? (
              <EditorialProseBody report={report} sectionId="projectIntentOverview" theme={theme} />
            ) : overviewParas.length > 0 ? (
              overviewParas.map((para, i) => (
                <p key={i} className="m-0 whitespace-pre-wrap break-words">
                  {walkClaimsText(para, report.references, report.claims)}
                </p>
              ))
            ) : (
              <KeyFindingsEmptyState
                message={placeholderCopy}
                isAdmin={isAdmin}
                onRegenerate={onSectionRegen}
                exportMode={exportMode}
              />
            )}
          </div>
        </div>
      </div>

      {showExecSummaryFindings && (
        <ContextualKeyFindingsBlock
          report={report}
          theme={theme}
          className="mt-6"
        />
      )}
    </EditorialSectionShell>
  );
};
